import {
  Box,
  Divider,
  // eslint-disable-next-line no-restricted-imports
  Button as ChakraButton,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

export const MarketingAuditContent = () => (
  <Box>
    {/* Introduction */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="20px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        fontFamily="marketingPage">
        Introduction
      </Box>
      <Box mb={4}>
        In today&apos;s economy,{' '}
        <Box as="span" fontWeight="bold">
          marketing is under the same scrutiny as every other investment
        </Box>
        . Budgets are tighter, growth targets remain high, and every euro spent
        must prove its value on the bottom line.
      </Box>
      <Box mb={4}>
        For CEOs and business leaders, the key question is no longer{' '}
        <Box as="span" fontStyle="italic">
          &ldquo;How much are we spending?&rdquo;
        </Box>{' '}
        - it&apos;s{' '}
        <Box as="span" fontWeight="bold">
          &ldquo;What do we get back?&rdquo;
        </Box>
      </Box>
      <Box mb={4}>
        That&apos;s why Yapamar created the{' '}
        <Box as="span" fontWeight="bold">
          Marketing Growth Audit
        </Box>{' '}
        a six-month, data-driven audit that reveals{' '}
        <Box as="span" fontWeight="bold">
          exactly where your marketing spend drives sales, where it
          doesn&apos;t, and how to reallocate budget for maximum business impact
        </Box>
        .
      </Box>
      <Box mb={4}>
        This is not theory. It&apos;s a{' '}
        <Box as="span" fontWeight="bold">
          practical modeling-based approach
        </Box>{' '}
        that delivers what leaders need most:
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Proof
          </Box>{' '}
          of what works
        </Box>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Savings
          </Box>{' '}
          from what doesn&apos;t
        </Box>
        <Box as="li" mb={2}>
          And a{' '}
          <Box as="span" fontWeight="bold">
            roadmap to higher ROI
          </Box>
          , without increasing spend
        </Box>
      </Box>
      <Box mb={4}>
        In short: it helps you{' '}
        <Box as="span" fontWeight="bold">
          do more with less
        </Box>{' '}
        and{' '}
        <Box as="span" fontWeight="bold">
          defend every euro
        </Box>
        .
      </Box>
    </Box>
    <Divider my={12} />

    {/* Step 1 */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Step 1: See the Full Picture of Your Marketing Investment
      </Box>
      <Box mb={4}>
        Most companies operate with fragmented reporting: one system for
        digital, another for offline, promotions, pricing, and brand. This makes
        it nearly impossible to know{' '}
        <Box as="span" fontWeight="bold">
          where sales actually come from
        </Box>
        .
      </Box>
      <Box mb={4}>
        The Yapamar audit consolidates all marketing investments into one{' '}
        <Box as="span" fontWeight="bold">
          unified, omnichannel view
        </Box>{' '}
        and refreshes the data continuously and gives you results during your
        first month on where to start optimizing.
      </Box>
      <Box mb={4}>
        <Box as="span" fontStyle="italic">
          Result:
        </Box>{' '}
        A{' '}
        <Box as="span" fontWeight="bold">
          single source of truth
        </Box>
        , showing where your budget actually goes, and which channels truly earn
        their share of revenue.
      </Box>
    </Box>
    <Divider my={12} />

    {/* Step 2 */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Step 2: Measure the Balance Between Short-Term Revenue and Long-Term
        Value
      </Box>
      <Box mb={4}>
        Yapamar classifies every activity by{' '}
        <Box as="span" fontWeight="bold">
          time horizon and purpose
        </Box>
        :
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Activation:
          </Box>{' '}
          actions that deliver immediate sales
        </Box>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Brand-building:
          </Box>{' '}
          actions that create future demand and margin strength
        </Box>
      </Box>
      <Box mb={4}>
        Tracking this balance monthly exposes{' '}
        <Box as="span" fontWeight="bold">
          where the company may be over-prioritizing short-term gains
        </Box>{' '}
        at the expense of long-term brand equity and profitability.
      </Box>
      <Box mb={4}>
        <Box as="span" fontStyle="italic">
          Result:
        </Box>{' '}
        Clarity on how to{' '}
        <Box as="span" fontWeight="bold">
          stabilize growth
        </Box>
        , not just chase quarterly spikes.
      </Box>
    </Box>
    <Divider my={12} />

    {/* Step 3 */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Step 3: Identify Where Budget Is Wasted and How to Reinvest It
      </Box>
      <Box mb={4}>
        A six-month audit reveals patterns that one-off reports miss:
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          Are{' '}
          <Box as="span" fontWeight="bold">
            promotions eating into baseline sales
          </Box>{' '}
          instead of growing them?
        </Box>
        <Box as="li" mb={2}>
          Are{' '}
          <Box as="span" fontWeight="bold">
            high-performing channels or campaigns driving profit
          </Box>{' '}
          or just visibility?
        </Box>
        <Box as="li" mb={2}>
          Is your budget{' '}
          <Box as="span" fontWeight="bold">
            trapped in low-yield activities?
          </Box>
        </Box>
      </Box>
      <Box mb={4}>
        <Box as="span" fontStyle="italic">
          Result:
        </Box>{' '}
        Confidence to{' '}
        <Box as="span" fontWeight="bold">
          cut what doesn&apos;t pay off
        </Box>{' '}
        and double down on what does.
      </Box>
      <Box mb={4}>
        Every euro saved from underperforming media is{' '}
        <Box as="span" fontWeight="bold">
          a euro reinvested into growth
        </Box>
        .
      </Box>
    </Box>
    <Divider my={12} />

    {/* Step 4 */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Step 4: Model the True Drivers of Sales and Growth
      </Box>
      <Box mb={4}>
        Yapamar runs{' '}
        <Box as="span" fontWeight="bold">
          two parallel models
        </Box>{' '}
        that connect marketing activity directly to business outcomes:
      </Box>
      <Box as="ol" mb={4} pl={6}>
        <Box as="li" mb={2}>
          A{' '}
          <Box as="span" fontWeight="bold">
            short-term model
          </Box>{' '}
          showing what drives sales this quarter
        </Box>
        <Box as="li" mb={2}>
          A{' '}
          <Box as="span" fontWeight="bold">
            long-term model
          </Box>{' '}
          showing what builds future demand, margin, and resilience
        </Box>
      </Box>
      <Box mb={4}>
        Updated continuously, these models reveal{' '}
        <Box as="span" fontWeight="bold">
          which campaigns, channels, and tactics deliver measurable business
          impact
        </Box>{' '}
        not just marketing metrics.
      </Box>
      <Box mb={4}>
        <Box as="span" fontStyle="italic">
          Result:
        </Box>{' '}
        The ability to{' '}
        <Box as="span" fontWeight="bold">
          see exactly where your marketing investment drives sales and profit
        </Box>
        .
      </Box>
    </Box>
    <Divider my={12} />

    {/* Step 5 */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Step 5: Build Confidence with Data You Can Trust
      </Box>
      <Box mb={4}>
        No single metric tells the full story. That&apos;s why Yapamar{' '}
        <Box as="span" fontWeight="bold">
          triangulates multiple data sources
        </Box>
        , from MMM and brand tracking to experiments and omnichannel performance
        signals.
      </Box>
      <Box mb={4}>
        <Box as="span" fontStyle="italic">
          Result:
        </Box>{' '}
        A{' '}
        <Box as="span" fontWeight="bold">
          robust, evidence-based view
        </Box>{' '}
        of marketing&apos;s contribution to sales trusted by finance, marketing,
        and the board alike.
      </Box>
    </Box>
    <Divider my={12} />

    {/* Step 6 */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Step 6: Translate Marketing Impact into Business Language
      </Box>
      <Box mb={4}>
        Executives don&apos;t speak in clicks, impressions, or ROAS, they speak
        in{' '}
        <Box as="span" fontWeight="bold">
          Return on Marketing Investment (ROMI)
        </Box>
        .
      </Box>
      <Box mb={4}>
        The Yapamar audit delivers insights in{' '}
        <Box as="span" fontWeight="bold">
          financial terms
        </Box>
        , showing the{' '}
        <Box as="span" fontWeight="bold">
          incremental revenue, profit, and cashflow
        </Box>{' '}
        driven by marketing, across both short and long timeframes. This lets
        you act at high or very granular level
      </Box>
      <Box mb={4}>
        <Box as="span" fontStyle="italic">
          Result:
        </Box>{' '}
        You gain the financial clarity to make{' '}
        <Box as="span" fontWeight="bold">
          investment-grade marketing decisions
        </Box>{' '}
        and show the board how marketing drives growth.
      </Box>
    </Box>
    <Divider my={12} />

    {/* The Yapamar Difference */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        The Yapamar Difference
      </Box>
      <Box mb={4}>
        <Box as="span" fontWeight="bold">
          Business Efficiency First:
        </Box>{' '}
        See where every euro drives growth and where it&apos;s wasted.
      </Box>
      <Box mb={4}>
        <Box as="span" fontWeight="bold">
          Full Investment View
        </Box>
        : Integrates all marketing activity, online and offline.
      </Box>
      <Box mb={4}>
        <Box as="span" fontWeight="bold">
          Optimizing Result:
        </Box>{' '}
        Start optimizing your marketing activity during the first month
      </Box>
      <Box mb={4}>
        <Box as="span" fontWeight="bold">
          Continuous, Six-Month Audit:
        </Box>{' '}
        Identifies patterns, not anomalies.
      </Box>
      <Box mb={4}>
        <Box as="span" fontWeight="bold">
          Dual Modelling:
        </Box>{' '}
        Balances short-term sales and long-term brand value.{' '}
        <Box as="span" fontWeight="bold">
          Immediate ROI Gains:
        </Box>{' '}
        Reallocate spend to deliver{' '}
        <Box as="span" fontWeight="bold">
          more sales from the same budget
        </Box>
        .
      </Box>
    </Box>
    <Divider my={12} />

    {/* 6-Month Business Impact Plan */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        6-Month Business Impact Plan
      </Box>
      <Box
        as="table"
        width="100%"
        mb={8}
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          border: '1px solid',
          borderColor: 'gray.200',
        }}>
        <Box as="thead" bg="gray.50">
          <Box as="tr">
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Month
            </Box>
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Focus
            </Box>
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Outcome
            </Box>
          </Box>
        </Box>
        <Box as="tbody">
          <Box as="tr">
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              1
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Data onboarding, investment mapping and model build
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Modeling setup and model build
            </Box>
          </Box>
          <Box as="tr" bg="gray.50">
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              1
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Initial audit results
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Initial results and budget reallocation scenarios
            </Box>
          </Box>
          <Box as="tr">
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              2-3
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Activation and model updates
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Updated ROI insights
            </Box>
          </Box>
          <Box as="tr" bg="gray.50">
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              4
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Midpoint review
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Budget reallocation scenarios
            </Box>
          </Box>
          <Box as="tr">
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              5-6
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Continuous tracking & updates
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Efficiency gains & clarity
            </Box>
          </Box>
          <Box as="tr" bg="gray.50">
            <Box as="td" p={4}>
              End
            </Box>
            <Box as="td" p={4}>
              Executive readout
            </Box>
            <Box as="td" p={4}>
              Strategic roadmap for growth & savings
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
    <Divider my={12} />

    {/* CTA Section */}
    <Box
      bg="background.lightGrey"
      borderRadius="16px"
      p={{ base: 6, md: 8 }}
      textAlign="center"
      mt={8}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        fontFamily="marketingPage">
        Ready to Audit Your Marketing Performance
      </Box>
      <Box mb={6} color="text.darkBlue.800" fontSize="18px">
        Choose our{' '}
        <Box as="span" fontWeight="bold">
          OMNICHANNEL GROWTH
        </Box>{' '}
        package to audit and unlock comprehensive insights across all your
        marketing channels.
      </Box>
      <ChakraButton
        as={RouterLink}
        to="/pricing"
        size="lg"
        bg="icon.green"
        color="white"
        fontWeight="bold"
        fontFamily="marketingPage"
        borderRadius="8px"
        px={8}
        _hover={{ bg: 'icon.green.500' }}>
        View Pricing Plans
      </ChakraButton>
    </Box>
  </Box>
);
